import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import Select from 'react-select';
import { Chart as ChartJS, ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, LineElement, PointElement, Filler } from 'chart.js';
import { Pie, Bar, Line } from 'react-chartjs-2';
import Api from '../Auth/Api';
import { customStyles } from '../utils/selectStyles';
import { useAjustes } from '../Context/AjustesContext';

ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, LineElement, PointElement, Filler);

const NIVEL_COLORS = {
    'Aluno':          { bg: 'rgba(59,130,246,0.8)',  border: '#3b82f6' },
    'Gestor':         { bg: 'rgba(16,185,129,0.8)',  border: '#10b981' },
    'Administrador':  { bg: 'rgba(139,92,246,0.8)',  border: '#8b5cf6' },
    'RH':             { bg: 'rgba(245,158,11,0.8)',  border: '#f59e0b' },
};

const MONTH_LABELS = { '01':'Jan','02':'Fev','03':'Mar','04':'Abr','05':'Mai','06':'Jun','07':'Jul','08':'Ago','09':'Set','10':'Out','11':'Nov','12':'Dez' };

function StatCard({ icon, label, value, color = '#3b82f6', loading }) {
    return (
        <div className="col-6 col-md-4 col-lg-2">
            <div className={`card border-0 shadow-sm h-100`}>
                <div className="card-body p-2 p-md-3 d-flex align-items-center gap-2 gap-md-3">
                    <div className="rounded-3 p-2 d-flex align-items-center justify-content-center flex-shrink-0" style={{ minWidth: 44, height: 44, backgroundColor: `${color}1a` }}>
                        <i className={`bi bi-${icon} fs-5`} style={{ color }}></i>
                    </div>
                    <div className="min-w-0">
                        <div className="text-muted fs-7">{label}</div>
                        {loading
                            ? <div className="placeholder-glow"><span className="placeholder col-6"></span></div>
                            : <div className="fw-bold fs-5">{value?.toLocaleString('pt-BR')}</div>
                        }
                    </div>
                </div>
            </div>
        </div>
    );
}

function DashboardEmpresa() {
    const { id: idParam } = useParams();
    const token = JSON.parse(localStorage.getItem('user_token'));
    const { ajustes } = useAjustes();
    const corPrimaria = ajustes?.cor_primaria || '#3a5a6c';

    const [empresas, setEmpresas] = useState([]);
    const [selectedId, setSelectedId] = useState(idParam ? Number(idParam) : null);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [erro, setErro] = useState(null);

    // Lista de empresas para o seletor
    useEffect(() => {
        let cancelled = false;
        const fetchEmpresas = async () => {
            try {
                const res = await Api.get('api/empresas', {
                    headers: { Authorization: `Bearer ${token}` }
                });
                if (!cancelled) setEmpresas(res.data.filter(e => e.status === 'ativo'));
            } catch (e) {
                /* silencioso — o seletor apenas fica vazio */
            }
        };
        fetchEmpresas();
        return () => { cancelled = true; };
    }, [token]);

    // Dados do dashboard da empresa selecionada
    useEffect(() => {
        if (!selectedId) { setData(null); setErro(null); return; }
        let cancelled = false;
        const fetchDashboard = async () => {
            setLoading(true);
            setErro(null);
            try {
                const res = await Api.get(`api/empresas/${selectedId}/dashboard`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                if (!cancelled) setData(res.data);
            } catch (e) {
                if (!cancelled) setErro(e.response?.data?.message || 'Erro ao carregar dashboard.');
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        fetchDashboard();
        return () => { cancelled = true; };
    }, [selectedId, token]);

    const empresaOptions = empresas.map(e => ({ value: e.id, label: e.nome }));
    const selectedOption = empresaOptions.find(o => o.value === selectedId) || null;

    const nivelChartData = data ? {
        labels: data.usuarios_por_nivel.map(n => n.nivel),
        datasets: [{
            data: data.usuarios_por_nivel.map(n => n.total),
            backgroundColor: data.usuarios_por_nivel.map(n => NIVEL_COLORS[n.nivel]?.bg || 'rgba(156,163,175,0.8)'),
            borderColor: data.usuarios_por_nivel.map(n => NIVEL_COLORS[n.nivel]?.border || '#9ca3af'),
            borderWidth: 2,
        }]
    } : null;

    const topCursosData = data ? {
        labels: data.top_cursos.map(c => c.nome.length > 25 ? c.nome.slice(0, 25) + '…' : c.nome),
        datasets: [{
            label: 'Alunos',
            data: data.top_cursos.map(c => c.total_alunos),
            backgroundColor: `${corPrimaria}b3`,
            borderColor: corPrimaria,
            borderWidth: 2,
            borderRadius: 6,
        }]
    } : null;

    const certsMesData = data ? (() => {
        const labels = data.certificados_por_mes.map(m => {
            const [, mes] = m.mes.split('-');
            return MONTH_LABELS[mes] || m.mes;
        });
        return {
            labels,
            datasets: [{
                label: 'Certificados',
                data: data.certificados_por_mes.map(m => m.total),
                backgroundColor: 'rgba(16,185,129,0.15)',
                borderColor: '#10b981',
                borderWidth: 2,
                fill: true,
                tension: 0.4,
                pointBackgroundColor: '#10b981',
            }]
        };
    })() : null;

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { display: false } },
        scales: { y: { beginAtZero: true, ticks: { precision: 0 } } }
    };

    const pieOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { position: 'bottom', labels: { padding: 16, boxWidth: 12 } } }
    };

    return (
        <div className="container-fluid py-4 px-3 px-md-4">

            {/* Cabeçalho com seletor de empresa */}
            <div className="d-flex align-items-end justify-content-between mb-4 flex-wrap gap-3">
                <div>
                    <h4 className="mb-1 fw-bold"><i className="bi bi-speedometer2 me-2 text-primary" />Dashboard</h4>
                    <small className="text-muted">Visão geral dos indicadores por empresa</small>
                </div>
                <div className="w-100" style={{ maxWidth: 420, flex: '1 1 280px' }}>
                    <label className="form-label text-muted fs-7 mb-1">Empresa</label>
                    <Select
                        options={empresaOptions}
                        value={selectedOption}
                        onChange={(opt) => setSelectedId(opt?.value ?? null)}
                        styles={customStyles}
                        placeholder="Selecione uma empresa..."
                        noOptionsMessage={() => 'Nenhuma empresa ativa'}
                        isClearable
                    />
                </div>
            </div>

            {/* Estado vazio — nenhuma empresa selecionada */}
            {!selectedId ? (
                <div className="card border-0 shadow-sm">
                    <div className="card-body text-center py-5">
                        <i className="bi bi-building fs-1 text-primary opacity-50 d-block mb-3"></i>
                        <h5 className="fw-semibold mb-1">Selecione uma empresa</h5>
                        <p className="text-muted mb-0">Escolha uma empresa no seletor acima para visualizar os indicadores.</p>
                    </div>
                </div>
            ) : erro ? (
                <div className="alert alert-danger d-flex align-items-center gap-2">
                    <i className="bi bi-exclamation-triangle-fill"></i>
                    <span>{erro}</span>
                </div>
            ) : (
                <>
                    {/* Informações da empresa */}
                    <div className="card border-0 shadow-sm mb-4">
                        <div className="card-body p-3">
                            <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-2">
                                {loading
                                    ? <div className="placeholder-glow"><span className="placeholder" style={{ width: 200, height: 22, borderRadius: 6 }}></span></div>
                                    : <h5 className="mb-0 fw-semibold text-primary">{data?.empresa?.nome}</h5>
                                }
                                {data?.empresa && (
                                    <span className={`badge ${data.empresa.status === 'ativo' ? 'bg-success' : 'bg-secondary'} px-3 py-2 fs-7`}>
                                        {data.empresa.status === 'ativo' ? 'Ativa' : 'Inativa'}
                                    </span>
                                )}
                            </div>
                            {data?.empresa && (
                                <div className="row g-2 text-muted fs-7">
                                    {data.empresa.cnpj && (
                                        <div className="col-auto"><i className="bi bi-file-earmark-text me-1"></i> {data.empresa.cnpj}</div>
                                    )}
                                    {data.empresa.telefone && (
                                        <div className="col-auto"><i className="bi bi-telephone me-1"></i> {data.empresa.telefone}</div>
                                    )}
                                    {data.empresa.nome_responsavel && (
                                        <div className="col-auto"><i className="bi bi-person me-1"></i> {data.empresa.nome_responsavel}</div>
                                    )}
                                    {data.empresa.endereco && (
                                        <div className="col-auto"><i className="bi bi-geo-alt me-1"></i> {data.empresa.endereco}</div>
                                    )}
                                    <div className="col-auto ms-auto">
                                        <i className="bi bi-calendar3 me-1"></i>
                                        Criada em {new Date(data.empresa.criado_em).toLocaleDateString('pt-BR')}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Cards de totais */}
                    <div className="row g-3 mb-4">
                        <StatCard icon="people-fill"          label="Usuários"          value={data?.totais?.usuarios}          color="#3b82f6"  loading={loading} />
                        <StatCard icon="person-fill"          label="Alunos"            value={data?.totais?.alunos}            color="#06b6d4"  loading={loading} />
                        <StatCard icon="person-gear"          label="Administradores"   value={data?.totais?.administradores}   color="#8b5cf6"  loading={loading} />
                        <StatCard icon="book-fill"            label="Cursos"            value={data?.totais?.cursos}            color="#f59e0b"  loading={loading} />
                        <StatCard icon="award-fill"           label="Certificados"      value={data?.totais?.certificados}      color="#10b981"  loading={loading} />
                        <StatCard icon="check-circle-fill"    label="Aulas Concluídas"  value={data?.totais?.aulas_concluidas}  color="#ef4444"  loading={loading} />
                    </div>

                    {/* Gráficos — linha 1 */}
                    <div className="row g-4 mb-4">
                        {/* Usuários por nível */}
                        <div className="col-md-4">
                            <div className="card border-0 shadow-sm h-100">
                                <div className="card-body p-3 p-md-4">
                                    <h6 className="fw-semibold text-primary mb-3">
                                        <i className="bi bi-pie-chart me-2"></i>Usuários por nível
                                    </h6>
                                    {loading ? (
                                        <div className="d-flex justify-content-center align-items-center" style={{ height: 220 }}>
                                            <div className="spinner-border text-primary"></div>
                                        </div>
                                    ) : nivelChartData && nivelChartData.labels.length > 0 ? (
                                        <div style={{ height: 220 }}>
                                            <Pie data={nivelChartData} options={pieOptions} />
                                        </div>
                                    ) : (
                                        <div className="d-flex justify-content-center align-items-center text-muted" style={{ height: 220 }}>
                                            Nenhum usuário cadastrado
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Top cursos */}
                        <div className="col-md-8">
                            <div className="card border-0 shadow-sm h-100">
                                <div className="card-body p-3 p-md-4">
                                    <h6 className="fw-semibold text-primary mb-3">
                                        <i className="bi bi-bar-chart me-2"></i>Top 5 cursos com mais alunos
                                    </h6>
                                    {loading ? (
                                        <div className="d-flex justify-content-center align-items-center" style={{ height: 220 }}>
                                            <div className="spinner-border text-primary"></div>
                                        </div>
                                    ) : topCursosData && topCursosData.labels.length > 0 ? (
                                        <div style={{ height: 220 }}>
                                            <Bar data={topCursosData} options={chartOptions} />
                                        </div>
                                    ) : (
                                        <div className="d-flex justify-content-center align-items-center text-muted" style={{ height: 220 }}>
                                            Nenhum curso cadastrado
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Gráficos — linha 2 */}
                    <div className="row g-4 mb-4">
                        {/* Certificados por mês */}
                        <div className="col-md-8">
                            <div className="card border-0 shadow-sm h-100">
                                <div className="card-body p-3 p-md-4">
                                    <h6 className="fw-semibold text-primary mb-3">
                                        <i className="bi bi-graph-up me-2"></i>Certificados emitidos (últimos 12 meses)
                                    </h6>
                                    {loading ? (
                                        <div className="d-flex justify-content-center align-items-center" style={{ height: 220 }}>
                                            <div className="spinner-border text-primary"></div>
                                        </div>
                                    ) : certsMesData && certsMesData.labels.length > 0 ? (
                                        <div style={{ height: 220 }}>
                                            <Line data={certsMesData} options={{ ...chartOptions, plugins: { legend: { display: false } } }} />
                                        </div>
                                    ) : (
                                        <div className="d-flex justify-content-center align-items-center text-muted" style={{ height: 220 }}>
                                            Nenhum certificado emitido nos últimos 12 meses
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Gestores / Admins */}
                        <div className="col-md-4">
                            <div className="card border-0 shadow-sm h-100">
                                <div className="card-body p-3 p-md-4">
                                    <h6 className="fw-semibold text-primary mb-3">
                                        <i className="bi bi-person-badge me-2"></i>Gestores e administradores
                                    </h6>
                                    {loading ? (
                                        <div className="d-flex justify-content-center align-items-center" style={{ height: 120 }}>
                                            <div className="spinner-border text-primary"></div>
                                        </div>
                                    ) : data?.administradores?.length > 0 ? (
                                        <ul className="list-unstyled mb-0">
                                            {data.administradores.map((a, i) => (
                                                <li key={i} className="d-flex align-items-center gap-2 py-2 border-bottom border-light-subtle">
                                                    <i className="bi bi-person-circle text-secondary fs-5"></i>
                                                    <div className="overflow-hidden">
                                                        <div className="fw-medium text-truncate" style={{ maxWidth: 200 }}>{a.nome_completo}</div>
                                                        <div className="text-muted fs-7 text-truncate" style={{ maxWidth: 200 }}>{a.email || a.codigo}</div>
                                                    </div>
                                                </li>
                                            ))}
                                        </ul>
                                    ) : (
                                        <div className="text-muted fs-7">Nenhum administrador cadastrado.</div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}

export default DashboardEmpresa;
